import { type GetApiTagsQueryParams } from '@/shared/api'
import { combine, createStore, sample } from 'effector'
import { createGate } from 'effector-react'

import { tagCreate, tagDelete, tagUpdate } from '../api'
import {
  type TagsQueryModelWithPagination,
  createTagsQueryModel,
} from '../lib/tags-query-model'

interface TagsTableGateProps {
  initialFilters?: GetApiTagsQueryParams
  initialPage?: number
  initialPageSize?: number
  initialSearch?: string
}

export const TagsTableGate = createGate<TagsTableGateProps>()

export const TagsTableTagsQueryModel = createTagsQueryModel({
  usePagination: true,
  accumulate: false,
}) as TagsQueryModelWithPagination
const { availableSet, unavailableSet } = TagsTableTagsQueryModel
export const {
  //   Query
  query: tagsTableQuery,
  //   Init
  $filters: $tagsTableFilters,
  filtersInit: tagsTableFiltersInit,
  //   Search
  $search: $tagsTableSearch,
  searchChanged: tagsTableSearchChanged,
  searchReset: tagsTableSearchReset,
  //   Pagination
  $page: $tagsTablePage,
  pageChanged: tagsTablePageChanged,
  pageReset: tagsTablePageReset,
  $pageSize: $tagsTablePageSize,
  pageSizeChanged: tagsTablePageSizeChanged,
  //   Refresh
  refreshed: tagsTableRefreshed,
} = TagsTableTagsQueryModel

sample({
  clock: TagsTableGate.open,
  target: availableSet,
})

// Search input

export const $tagsTableSearchInputValue = createStore('')
  .on(tagsTableSearchChanged, (_, q) => q)
  .reset(tagsTableSearchReset)

// Setup initial params
// Page
sample({
  clock: TagsTableGate.open,
  source: combine(
    TagsTableGate.state,
    ({ initialPage }) => initialPage || null,
  ),
  filter: (page) => page !== null,
  target: tagsTablePageChanged,
})
// Page Size
sample({
  clock: TagsTableGate.open,
  source: combine(
    TagsTableGate.state,
    ({ initialPageSize }) => initialPageSize || null,
  ),
  filter: (pageSize) => pageSize !== null,
  target: tagsTablePageSizeChanged,
})
// Search
sample({
  clock: TagsTableGate.open,
  source: combine(
    TagsTableGate.state,
    ({ initialSearch }) => initialSearch || null,
  ),
  filter: (search) => search !== null,
  target: tagsTableSearchChanged,
})
// Filters
sample({
  source: TagsTableGate.state,
  clock: availableSet,
  fn: ({ initialFilters: params }) => params || {},
  target: tagsTableFiltersInit,
})

sample({
  clock: TagsTableGate.close,
  target: unavailableSet,
})

// Decrease page if there is only one record on page and it's being deleted
sample({
  source: combine({
    page: $tagsTablePage,
    queryData: tagsTableQuery.$data,
  }),
  clock: tagDelete.finished.success,
  filter: ({ queryData, page }) =>
    !queryData?.items.length || (queryData?.items.length <= 1 && page > 1),
  fn: ({ page }) => page - 1,
  target: tagsTablePageChanged,
})

sample({
  clock: [
    tagCreate.finished.success,
    tagUpdate.finished.success,
    tagDelete.finished.success,
  ],
  target: tagsTableRefreshed,
})
